import { ChangeEvent, useReducer } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import basicElementIcon from '@atom/components/common/svgIcons/elementIcon.svg';
import { Icon, Menu, Modal } from '@atom/mui';
import {
  DeleteIcon,
  KeyboardArrowUpIcon,
  ReportProblemIcon,
  VerifiedUserIcon,
} from '@atom/mui/icons';
import {
  buildFhwaUpdateBody,
  equalizeFhwaPercentValues,
  equalizeFhwaValues,
  isFhwaValid,
} from '@atom/selectors/formModuleSelectors';
import {
  DEFECT_MARKER_ID,
  END_JOINT_MARKER_ID,
  PROTECTIVE_COATING_MARKER_ID,
  WEARING_SURFACE_MARKER_ID,
} from '@atom/selectors/schemaSelectors';
import colors from '@atom/styles/colors';
import { FhwaModuleElementType } from '@atom/types/form';
import { Media } from '@atom/types/media';
import iconUtilities from '@atom/utilities/iconUtilities';
import { isCbitFhwaEnabled } from '@atom/utilities/tenantFeatureUtilities';

import FhwaAddAssetElementModal from './FhwaAddAssetElementModal';
import FhwaDropDown from './FhwaDropDown';
import {
  FhwaFieldRowState,
  FhwaFieldRowStateReducer,
  ModalType,
} from './FhwaFieldRowState';
import FhwaInput from './FhwaInput';
import FhwaMediaModal from './FhwaMediaModal';
import FhwaRemarksModal from './FhwaRemarksModal';

import classes from '../formInstance.module.css';

const { MenuItem } = Menu;

interface Props {
  csType: string;
  element: FhwaModuleElementType;
  isEditable: boolean;
  fieldId: string;
  assetId: string;
  media: Media[];
  isUploadDisabled: boolean;
  indentLevel: number;
  elementPath: any[];
  uploadFiles: (
    files?: any[] | FileList,
    subjectId?: string,
    subjectType?: string,
  ) => void;
  removeFile: (mediaId: string, subjectId: string) => void;
  renameMedia: (mediaId: string, subjectId: string, name: string) => void;
  progressiveUpdateFormInstanceFhwa: (
    fieldId: string,
    assetId: string,
    body: Object,
    elementPath: any[],
  ) => void;
  onBatchCreate: (payload: Object) => void;
  onElementDelete: (assetId: string) => boolean;
}

const FhwaFieldRow = ({
  element,
  csType,
  isEditable,
  isUploadDisabled,
  media,
  assetId,
  removeFile,
  renameMedia,
  progressiveUpdateFormInstanceFhwa,
  uploadFiles,
  fieldId,
  indentLevel,
  elementPath,
  onBatchCreate,
  onElementDelete,
}: Props) => {
  const { attributes } = element;
  // TODO: If good, refactor to external getInitState method
  const [state, dispatchState] = useReducer(FhwaFieldRowStateReducer, {
    environment: R.pathOr(null, ['value'], attributes.environment),
    totalQuantity: R.pathOr(0, ['value'], attributes.totalQuantity),
    remarks: R.pathOr('', ['value'], attributes.remarks),
    cs1: R.pathOr(0, ['value'], attributes.cs1),
    cs2: R.pathOr(0, ['value'], attributes.cs2),
    cs3: R.pathOr(0, ['value'], attributes.cs3),
    cs4: R.pathOr(0, ['value'], attributes.cs4),
    cs1Percent: R.pathOr(0, ['value'], attributes.cs1Percent),
    cs2Percent: R.pathOr(0, ['value'], attributes.cs2Percent),
    cs3Percent: R.pathOr(0, ['value'], attributes.cs3Percent),
    cs4Percent: R.pathOr(0, ['value'], attributes.cs4Percent),
    openModal: { type: ModalType.NONE, markerId: 0, id: null },
  });
  const {
    environment,
    totalQuantity,
    cs1,
    cs2,
    cs3,
    cs4,
    cs1Percent,
    cs2Percent,
    cs3Percent,
    cs4Percent,
    remarks,
    openModal,
  }: FhwaFieldRowState = state;

  const saveFhwa = (stateUpdate: Partial<FhwaFieldRowState> = {}) => {
    const body = buildFhwaUpdateBody({ ...state, ...stateUpdate }, element);
    progressiveUpdateFormInstanceFhwa(fieldId, assetId, body, elementPath);
  };

  const toggleModal = (modalType: ModalType, markerId: number, openId: any) => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { openModal: { type: modalType, markerId, id: openId } },
    });
  };

  const isModalOpen = (modalTypeArr: ModalType[], openId: any) => {
    const { type, id } = state.openModal;
    return modalTypeArr.includes(type) && id === openId;
  };

  const onDropDownChange = (event: any) => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { environment: event.target.value },
    });
    saveFhwa({ environment: event.target.value });
  };

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { name: attributeName, value } = event.target;

    const newValue = value === '' ? '' : Number(value);

    const updatedState = { ...state, [attributeName]: newValue };
    const equalizedValues =
      csType === '%'
        ? equalizeFhwaPercentValues(updatedState)
        : equalizeFhwaValues(updatedState);

    dispatchState({
      type: 'UPDATE_STATE',
      data: { ...equalizedValues, [attributeName]: newValue },
    });

    saveFhwa({ [attributeName]: newValue });
  };

  const saveRemarks = (newRemarks: string) => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { remarks: newRemarks },
    });

    saveFhwa({ remarks: newRemarks });
  };

  const assetFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const subjectType = 'formInstanceAsset';

    uploadFiles(files, assetId, subjectType);
  };

  const getIndentStyle = (): Object => {
    const BASE_INDENT_REMS = 0.75;
    const NESTED_INDENT_REMS = 1.75;

    const indentPadding = NESTED_INDENT_REMS * indentLevel;

    return {
      paddingLeft: `${BASE_INDENT_REMS + indentPadding}rem`,
    };
  };

  const onDelete = () => {
    onElementDelete(assetId);
  };

  const onConfirmDeleteModal = () => {
    onDelete();
    toggleModal(ModalType.NONE, 0, null);
  };

  const getModalTitle = (modalType: ModalType) => {
    switch (modalType) {
      case ModalType.ADD_DEFECT:
        return 'Add Defect';
      case ModalType.ADD_PROTECTIVE_COATING:
        return 'Add Protective Coating';
      case ModalType.ADD_END_JOINT:
        return 'Add End Joint';
      case ModalType.ADD_WEARING_SURFACE:
        return 'Add Wearing Surface';
      default:
        return '';
    }
  };

  const csName = csType === '%' ? 'Percent' : '';

  const cs1Value = csType === '%' ? cs1Percent : cs1;
  const cs2Value = csType === '%' ? cs2Percent : cs2;
  const cs3Value = csType === '%' ? cs3Percent : cs3;
  const cs4Value = csType === '%' ? cs4Percent : cs4;

  const isValid = isFhwaValid(state);

  const hasRemarks = !R.isEmpty(remarks);

  const unit = R.pathOr('', ['unit'], element.attributes.totalQuantity);

  const nestedElements = R.pathOr({}, ['elements'], element);
  const order = R.pathOr([], ['order'], element);

  const existingSchemaIdsList = order.map(id => nestedElements[id]?.schemaId);
  const existingSchemaIds = existingSchemaIdsList.reduce((id, index) => {
    id[index] = true;
    return id;
  }, {});

  const leftIcon = !R.isEmpty(nestedElements) ? (
    <Icon
      color={colors.neutral.gray}
      style={{ paddingRight: '5px' }}
      icon={KeyboardArrowUpIcon}
    />
  ) : (
    <div style={{ height: '24px', width: '24px', paddingRight: '5px' }} />
  );

  const elementIcon = iconUtilities.buildFHWAElementIcon(
    element.markerId,
    isValid,
  );

  const leftIndentStyle = getIndentStyle();

  const renderAddDefect =
    (element.markerId === PROTECTIVE_COATING_MARKER_ID ||
      R.isNil(element.markerId)) &&
    !isCbitFhwaEnabled;
  const renderAddProtectiveCoating = R.isNil(element.markerId);
  const renderAddEndJoint = isCbitFhwaEnabled;
  const renderAddWearingSurface = isCbitFhwaEnabled;

  return (
    <>
      <div className={classes.fhwaTableRow}>
        <div className={classes.elementColumn} style={leftIndentStyle}>
          <div className={classes.fhwaLeftIconBox}>
            {leftIcon}
            {elementIcon}
          </div>
          <div className={clsx(classes.elementName, !isValid && classes.red)}>
            {element.name}
          </div>
        </div>
        <div className={clsx(classes.largeColumn, classes.environment)}>
          <FhwaDropDown
            isEditable={isEditable}
            element={element}
            environment={environment}
            onChange={onDropDownChange}
          />
        </div>
        <div className={classes.largeColumn}>
          <FhwaInput
            name="totalQuantity"
            value={totalQuantity}
            isEditable={isEditable}
            onChange={onChange}
            isValid={isValid}
            width="5rem"
          />
          <div className={classes.columnSuffixText}>{unit}</div>
        </div>
        <div className={classes.cs1Column}>{cs1Value}</div>
        <div className={classes.normalColumn}>
          <FhwaInput
            name={`cs2${csName}`}
            value={cs2Value}
            isEditable={isEditable}
            onChange={onChange}
            isValid={isValid}
            width="3.8rem"
          />
        </div>
        <div className={classes.normalColumn}>
          <FhwaInput
            name={`cs3${csName}`}
            value={cs3Value}
            isEditable={isEditable}
            onChange={onChange}
            isValid={isValid}
            width="3.8rem"
          />
        </div>
        <div className={classes.normalColumn}>
          <FhwaInput
            name={`cs4${csName}`}
            value={cs4Value}
            isEditable={isEditable}
            onChange={onChange}
            isValid={isValid}
            width="3.8rem"
          />
        </div>
        <div className={classes.attachColumn}>
          <FhwaMediaModal
            isUploadDisabled={isUploadDisabled}
            isEditable={isEditable}
            media={media}
            assetFileUpload={assetFileUpload}
            removeFile={removeFile}
            assetId={assetId}
            renameMedia={renameMedia}
          />
        </div>
        <div className={clsx(classes.attachColumn, classes.right)}>
          <FhwaRemarksModal
            remarks={remarks}
            isEditable={isEditable}
            saveRemarks={saveRemarks}
            hasRemarks={hasRemarks}
          />
        </div>
        <div className={clsx(classes.attachColumn, classes.additional)}>
          {isEditable && (
            <Menu>
              {renderAddDefect && (
                <MenuItem
                  startAdornment={<Icon icon={ReportProblemIcon} />}
                  onClick={() =>
                    toggleModal(ModalType.ADD_DEFECT, DEFECT_MARKER_ID, assetId)
                  }
                >
                  Add Defect
                </MenuItem>
              )}
              {renderAddProtectiveCoating && (
                <MenuItem
                  startAdornment={<Icon icon={VerifiedUserIcon} />}
                  onClick={() =>
                    toggleModal(
                      ModalType.ADD_PROTECTIVE_COATING,
                      PROTECTIVE_COATING_MARKER_ID,
                      assetId,
                    )
                  }
                >
                  Add Protective Coating
                </MenuItem>
              )}
              {renderAddEndJoint && (
                <MenuItem
                  startAdornment={
                    <Icon>
                      <img src={basicElementIcon} />
                    </Icon>
                  }
                  onClick={() =>
                    toggleModal(
                      ModalType.ADD_END_JOINT,
                      END_JOINT_MARKER_ID,
                      assetId,
                    )
                  }
                >
                  Add End Joint
                </MenuItem>
              )}
              {renderAddWearingSurface && (
                <MenuItem
                  startAdornment={
                    <Icon>
                      <img src={basicElementIcon} />
                    </Icon>
                  }
                  onClick={() =>
                    toggleModal(
                      ModalType.ADD_WEARING_SURFACE,
                      WEARING_SURFACE_MARKER_ID,
                      assetId,
                    )
                  }
                >
                  Add Wearing Surface
                </MenuItem>
              )}
              <MenuItem
                startAdornment={<Icon icon={DeleteIcon} />}
                onClick={() => toggleModal(ModalType.DELETE, 0, assetId)}
              >
                Delete
              </MenuItem>
            </Menu>
          )}
        </div>
      </div>
      {order.map((nestedAssetId: string, index: number) => {
        const nextElementPath = [...elementPath, 'elements', nestedAssetId];
        const rowMedia = media.filter(
          medium => medium.fieldId === nestedAssetId,
        );

        return (
          <FhwaFieldRow
            csType={csType}
            key={`${nestedAssetId}-${index}`}
            element={nestedElements[nestedAssetId]}
            assetId={nestedAssetId}
            fieldId={fieldId}
            isEditable={isEditable}
            media={rowMedia}
            isUploadDisabled={isUploadDisabled}
            uploadFiles={uploadFiles}
            removeFile={removeFile}
            renameMedia={renameMedia}
            progressiveUpdateFormInstanceFhwa={
              progressiveUpdateFormInstanceFhwa
            }
            indentLevel={indentLevel + 1}
            elementPath={nextElementPath}
            onBatchCreate={onBatchCreate}
            onElementDelete={onElementDelete}
          />
        );
      })}
      <Modal
        open={isModalOpen([ModalType.DELETE], assetId)}
        onCancel={() => toggleModal(ModalType.NONE, 0, null)}
        title={`Delete ${element.name}?`}
        confirmButtonText="Delete"
        ConfirmButtonProps={{ sx: { background: colors.brand.red } }}
        onConfirm={onConfirmDeleteModal}
      >
        Are you sure you want to delete this element and all nested elements?
      </Modal>
      <FhwaAddAssetElementModal
        open={isModalOpen(
          [
            ModalType.ADD_DEFECT,
            ModalType.ADD_PROTECTIVE_COATING,
            ModalType.ADD_END_JOINT,
            ModalType.ADD_WEARING_SURFACE,
          ],
          assetId,
        )}
        closeModal={() => toggleModal(ModalType.NONE, 0, null)}
        title={getModalTitle(state.openModal.type)}
        assetId={assetId}
        assetName={element.name}
        onBatchCreate={onBatchCreate}
        existingSchemaIds={existingSchemaIds}
        markerId={openModal.markerId}
      />
    </>
  );
};

export default FhwaFieldRow;
